export const RAHU_KAAL_HEADLINES = { en: "Rahu Kaal Today", hi: "राहु काल आज" } as const;
import Link from "next/link";

import { Collapsible } from "@ishvaram/ui/Collapsible";

const WEEKLY_RAHU_ROWS = [
  { day: "Monday", hi: "सोमवार", seg: "2nd", time: "7:30 - 9:00 AM" },
  { day: "Saturday", hi: "शनिवार", seg: "3rd", time: "9:00 - 10:30 AM" },
  { day: "Friday", hi: "शुक्रवार", seg: "4th", time: "10:30 - 12:00 PM" },
  { day: "Wednesday", hi: "बुधवार", seg: "5th", time: "12:00 - 1:30 PM" },
  { day: "Thursday", hi: "गुरुवार", seg: "6th", time: "1:30 - 3:00 PM" },
  { day: "Tuesday", hi: "मंगलवार", seg: "7th", time: "3:00 - 4:30 PM" },
  { day: "Sunday", hi: "रविवार", seg: "8th", time: "4:30 - 6:00 PM" },
] as const;

type WeeklyRahuRow = Readonly<{ day: string; hi: string; seg: string; time: string }>;

/**
 * A city page's own weekly table: rows computed from that city's sunrise and
 * sunset (`cityWeeklyRahuReference`) and the note that names the sample date.
 * Without it the hub's generic 6:00 AM table renders.
 */
export type CityWeeklyRahuReference = Readonly<{
  rows: readonly WeeklyRahuRow[];
  note: string;
}>;

export function WeeklyRahuReference({
  dayName,
  cityReference,
  selectedWindow,
}: {
  dayName: string;
  cityReference?: CityWeeklyRahuReference | null;
  /**
   * The engine window for the selected date. On a city table the highlighted row
   * shows it instead of the sample-day estimate, so the row labelled TODAY can
   * never disagree with the headline above by a few minutes.
   */
  selectedWindow?: string;
}) {
  const rows: readonly WeeklyRahuRow[] = cityReference
    ? cityReference.rows.map((row) =>
        row.day === dayName && selectedWindow ? { ...row, time: selectedWindow } : row
      )
    : WEEKLY_RAHU_ROWS;
  return (
    <Collapsible title="Weekly Rahu Kaal Reference" defaultOpen={false} headingLevel="h2">
      <div className="card-sacred p-6 sm:p-8 mt-0">
        <p className="text-body-small mb-4 text-ish-muted">
          {cityReference?.note ??
            "Approximate times based on 6:00 AM sunrise. Use the calculator above for exact timings."}
        </p>
        <div className="space-y-1.5">
          {rows.map((row) => (
            <WeeklyRahuRow key={row.day} row={row} isToday={row.day === dayName} />
          ))}
        </div>
      </div>
    </Collapsible>
  );
}

function WeeklyRahuRow({ row, isToday }: { row: WeeklyRahuRow; isToday: boolean }) {
  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border ${
        isToday ? "bg-error/6 border-error/25" : "bg-black/2 border-ish-border-light"
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`text-body-small font-semibold ${isToday ? "text-error" : "text-ish-dark"}`}
        >
          {row.day}
        </span>
        <span className="text-caption text-ish-faint">{row.hi}</span>
        {isToday && (
          <span className="text-caption font-semibold px-1.5 py-0.5 rounded bg-error/12 text-error">
            TODAY
          </span>
        )}
      </div>
      <div className="text-right">
        <span
          className={`text-body-small font-semibold ${isToday ? "text-error" : "text-ish-dark"}`}
        >
          {row.time}
        </span>
        <span className="text-caption ml-1.5 text-ish-faint">({row.seg})</span>
      </div>
    </div>
  );
}

export function RahuSeoContent({ language = "en" }: { language?: "en" | "hi" } = {}) {
  return language === "hi" ? <RahuSeoContentHi /> : <RahuSeoContentEn />;
}

function RahuSeoContentEn() {
  return (
    <Collapsible
      title="About Rahu Kaal"
      titleHi="राहु काल के बारे में"
      defaultOpen={false}
      headingLevel="h2"
    >
      <div className="card-sacred p-6 sm:p-8 mt-0">
        <div className="space-y-3 text-body-small leading-relaxed text-ish-muted">
          <p>
            <strong className="text-ish-dark">Rahu Kaal</strong> (राहु काल) is one of the three
            inauspicious daily periods in Vedic astrology, alongside Yamagandam and Gulika Kaal. It
            lasts approximately 90 minutes each day and is ruled by the shadow planet Rahu — the
            north node of the Moon. Most Hindus avoid starting new ventures, signing documents, or
            beginning journeys during this period.
          </p>
          <p>
            The timing of Rahu Kaal changes every day because it depends on the day of the week and
            your local sunrise and sunset times. Our calculator uses the same astronomical engine
            (Swiss Ephemeris with Lahiri Ayanamsa) as the{" "}
            <Link href="/muhurat/" className="font-semibold text-intent-brand">
              Muhurat Calculator
            </Link>{" "}
            and{" "}
            <Link href="/panchang/" className="font-semibold text-intent-brand">
              Daily Panchang
            </Link>{" "}
            to give you precise, location-aware timings.
          </p>
          <p>
            For a complete picture of auspicious and inauspicious timings throughout the day,
            including hourly Muhurat analysis, visit our{" "}
            <Link href="/muhurat/" className="font-semibold text-intent-brand">
              Shubh Muhurat
            </Link>{" "}
            page.
          </p>
        </div>
      </div>
    </Collapsible>
  );
}

function RahuSeoContentHi() {
  return (
    <Collapsible title="राहु काल के बारे में" defaultOpen={false} headingLevel="h2">
      <div className="card-sacred p-6 sm:p-8 mt-0">
        <div className="space-y-3 text-body-small leading-relaxed text-ish-muted">
          <p>
            <strong className="text-ish-dark">राहु काल</strong> वैदिक ज्योतिष में प्रतिदिन आने वाली
            तीन अशुभ अवधियों में से एक है; बाकी दो यमगण्ड और गुलिक काल हैं। यह हर दिन लगभग 90 मिनट
            का होता है और इसका स्वामी छाया ग्रह राहु है, जो चंद्रमा का उत्तरी पात (नोड) है। अधिकांश
            हिंदू इस समय में नया काम शुरू करने, दस्तावेज़ों पर हस्ताक्षर करने या यात्रा आरंभ करने से
            बचते हैं।
          </p>
          <p>
            राहु काल का समय हर दिन बदलता है, क्योंकि यह सप्ताह के वार और आपके स्थान के सूर्योदय व
            सूर्यास्त पर निर्भर करता है। इसलिए किसी दूसरे शहर या दूसरी तारीख का समय अपने काम पर लागू
            न करें। उसी स्थान और तारीख के बाकी शुभ-अशुभ समय{" "}
            <Link href="/hi/muhurat/" className="font-semibold text-intent-brand">
              मुहूर्त कैलकुलेटर
            </Link>{" "}
            और{" "}
            <Link href="/hi/panchang/" className="font-semibold text-intent-brand">
              दैनिक पंचांग
            </Link>{" "}
            में देखें।
          </p>
          <p>
            पूरे दिन के शुभ और अशुभ समय, घंटेवार मुहूर्त विश्लेषण सहित, एक साथ देखने के लिए{" "}
            <Link href="/hi/muhurat/" className="font-semibold text-intent-brand">
              शुभ मुहूर्त
            </Link>{" "}
            पृष्ठ खोलें।
          </p>
        </div>
      </div>
    </Collapsible>
  );
}
